import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function TeacherFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { data: existingTeacher, isLoading: loadingTeacher } = useQuery<any>({
    queryKey: ['teacher', id],
    queryFn: () => api.get(`/api/teachers/${id}`).then(r => r.data),
    enabled: isEdit,
  });

  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    if (existingTeacher && !synced) {
      setFormData({
        name: existingTeacher.name || '',
        email: existingTeacher.email || '',
        phone: existingTeacher.phone || '',
        password: '',
      });
      setSynced(true);
    }
  }, [existingTeacher, synced]);

  const createMutation = useMutation({
    mutationFn: async (payload: any) => api.post('/api/teachers', payload),
    onSuccess: () => {
      toast.success('Teacher created');
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      navigate('/teachers');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create teacher');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: any) => api.patch(`/api/teachers/${id}`, payload),
    onSuccess: () => {
      toast.success('Teacher updated');
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      navigate('/teachers');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update teacher');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isEdit && loadingTeacher) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-violet-500" />
            {isEdit ? 'Edit Teacher' : 'Add Teacher'}
          </h1>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">{isEdit ? 'Update teacher details' : 'Create a new teacher account'}</p>
        </div>
      </div>

      <div className="glass-card p-4 sm:p-6 rounded-2xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-1">Name</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="h-10"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="h-10"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1">Password</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Default: teacher123456"
              className="h-10"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1">Phone</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="h-10"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1 h-10" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="flex-1 h-10">
              {createMutation.isPending || updateMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                isEdit ? 'Update' : 'Create'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
